import React, { useState, useEffect, useRef } from 'react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function AdminSupport() {
  const [tickets, setTickets] = useState([]);
  const [counts, setCounts] = useState({ open: 0, in_progress: 0, resolved: 0, total: 0 });
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [replyMessage, setReplyMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [filter, setFilter] = useState('all');
  const messagesEndRef = useRef(null);

  const token = localStorage.getItem('token');

  useEffect(() => {
    fetchTickets();
  }, [filter]);

  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [selectedTicket?.messages]);

  const fetchTickets = async () => {
    try {
      const url = filter === 'all' 
        ? `${API_URL}/api/admin/support-tickets`
        : `${API_URL}/api/admin/support-tickets?status=${filter}`;
      
      const response = await fetch(url, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      setTickets(data.tickets || []);
      setCounts(data.counts || {});
    } catch (error) {
      toast.error('Failed to load tickets');
    } finally {
      setLoading(false);
    }
  };

  const selectTicket = async (ticketId) => {
    try {
      const response = await fetch(`${API_URL}/api/admin/support-tickets/${ticketId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      setSelectedTicket(data);
    } catch (error) {
      toast.error('Failed to load ticket');
    }
  };

  const sendReply = async () => {
    if (!replyMessage.trim() || !selectedTicket) return;
    
    setSending(true);
    try {
      const response = await fetch(`${API_URL}/api/admin/support-tickets/${selectedTicket.id}/reply`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ message: replyMessage })
      });

      if (response.ok) {
        toast.success('Reply sent!');
        setReplyMessage('');
        selectTicket(selectedTicket.id);
        fetchTickets();
      } else {
        toast.error('Failed to send reply');
      }
    } catch (error) {
      toast.error('Failed to send reply');
    } finally {
      setSending(false);
    }
  };

  const updateStatus = async (ticketId, status) => {
    try {
      await fetch(`${API_URL}/api/admin/support-tickets/${ticketId}/status?status=${status}`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      toast.success(`Ticket marked as ${status}`);
      fetchTickets();
      if (selectedTicket?.id === ticketId) {
        selectTicket(ticketId);
      }
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      open: 'bg-red-100 text-red-800',
      in_progress: 'bg-yellow-100 text-yellow-800',
      resolved: 'bg-green-100 text-green-800'
    };
    return <Badge className={styles[status]}>{status.replace('_', ' ')}</Badge>;
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold">Support Tickets</h1>
        <div className="flex gap-2">
          <Badge variant="outline" className="bg-red-50">{counts.open} Open</Badge>
          <Badge variant="outline" className="bg-yellow-50">{counts.in_progress} In Progress</Badge>
          <Badge variant="outline" className="bg-green-50">{counts.resolved} Resolved</Badge>
        </div>
      </div>

      {/* Filter Tabs */}
      <div className="flex gap-2">
        {['all', 'open', 'in_progress', 'resolved'].map((f) => (
          <Button
            key={f}
            variant={filter === f ? 'default' : 'outline'}
            size="sm"
            onClick={() => setFilter(f)}
          >
            {f === 'all' ? 'All' : f.replace('_', ' ')}
          </Button>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Ticket List */}
        <div className="lg:col-span-1 space-y-2 max-h-[600px] overflow-y-auto">
          {tickets.length === 0 ? (
            <Card>
              <CardContent className="py-8 text-center text-gray-500">
                No tickets found
              </CardContent>
            </Card>
          ) : (
            tickets.map((ticket) => (
              <Card 
                key={ticket.id}
                className={`cursor-pointer transition-all hover:shadow-md ${
                  selectedTicket?.id === ticket.id ? 'ring-2 ring-blue-500' : ''
                }`}
                onClick={() => selectTicket(ticket.id)}
                data-testid={`ticket-${ticket.id}`}
              >
                <CardContent className="py-3">
                  <div className="flex justify-between items-start mb-2">
                    <h3 className="font-medium text-sm truncate flex-1">{ticket.subject}</h3>
                    {getStatusBadge(ticket.status)}
                  </div>
                  <p className="text-xs text-gray-500 mb-1">{ticket.name} • {ticket.email}</p>
                  <p className="text-xs text-gray-400">{formatDate(ticket.created_at)}</p>
                </CardContent>
              </Card>
            ))
          )}
        </div>

        {/* Chat View */}
        <div className="lg:col-span-2">
          {selectedTicket ? (
            <Card className="h-[600px] flex flex-col">
              <CardHeader className="border-b pb-3">
                <div className="flex justify-between items-start">
                  <div>
                    <CardTitle className="text-lg">{selectedTicket.subject}</CardTitle>
                    <p className="text-sm text-gray-500 mt-1">
                      {selectedTicket.name} • {selectedTicket.email}
                    </p>
                  </div>
                  <div className="flex gap-2">
                    {selectedTicket.status !== 'resolved' && (
                      <Button 
                        size="sm" 
                        variant="outline"
                        onClick={() => updateStatus(selectedTicket.id, 'resolved')}
                      >
                        Mark Resolved
                      </Button>
                    )}
                    {selectedTicket.status === 'resolved' && (
                      <Button 
                        size="sm" 
                        variant="outline"
                        onClick={() => updateStatus(selectedTicket.id, 'open')}
                      >
                        Reopen
                      </Button>
                    )}
                  </div>
                </div>
              </CardHeader>

              {/* Messages */}
              <CardContent className="flex-1 overflow-y-auto py-4 space-y-4">
                {selectedTicket.messages?.map((msg) => (
                  <div
                    key={msg.id}
                    className={`flex ${msg.from === 'admin' ? 'justify-end' : 'justify-start'}`}
                  >
                    <div
                      className={`max-w-[80%] rounded-lg px-4 py-2 ${
                        msg.from === 'admin'
                          ? 'bg-blue-600 text-white'
                          : 'bg-gray-100 text-gray-900'
                      }`}
                    >
                      <p className="text-sm whitespace-pre-wrap">{msg.message}</p>
                      <p className={`text-xs mt-1 ${
                        msg.from === 'admin' ? 'text-blue-200' : 'text-gray-500'
                      }`}>
                        {msg.name} • {formatDate(msg.timestamp)}
                      </p>
                    </div>
                  </div>
                ))}
                <div ref={messagesEndRef} />
              </CardContent>

              {/* Reply Input */}
              <div className="border-t p-4">
                <div className="flex gap-2">
                  <Textarea
                    value={replyMessage}
                    onChange={(e) => setReplyMessage(e.target.value)}
                    placeholder="Type your reply..."
                    rows={2}
                    className="flex-1"
                    data-testid="reply-input"
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && e.ctrlKey) {
                        sendReply();
                      }
                    }}
                  />
                  <Button 
                    onClick={sendReply} 
                    disabled={sending || !replyMessage.trim()}
                    data-testid="send-reply"
                  >
                    {sending ? 'Sending...' : 'Send'}
                  </Button>
                </div>
                <p className="text-xs text-gray-400 mt-1">Press Ctrl+Enter to send</p>
              </div>
            </Card>
          ) : (
            <Card className="h-[600px] flex items-center justify-center">
              <div className="text-center text-gray-500">
                <svg className="w-12 h-12 mx-auto mb-3 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
                </svg>
                <p>Select a ticket to view conversation</p>
              </div>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
}
